import { useState } from 'react';
import { Button, Dialog, InlineError, Input, Select, Textarea } from '../../components/ui';
import { DEFAULT_FIELD_COLOR, colorChoices } from './colors';
import {
  cardCountLabel,
  fieldUsage,
  nameProblem,
  optionProblem,
  useAddOption,
  useCreateField,
  useDeleteField,
  useDeleteOption,
  useRenameField,
  useSetOptionColor,
} from './manage';
import { useProjectFieldValues, useProjectFields } from './queries';
import type { FieldDefinition } from './fieldChips';

/**
 * Defining what a project's fields ARE, as opposed to setting them on a card.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * "I really like this template. Can we edit the code so other projects that
 * are non Social Media can do this too? this is the template I wanted all
 * along." Until now a field could only come into existence because a template
 * seeded it, which made the whole idea a property of one template rather than
 * a thing the app can do. This is the other half.
 *
 * ── Managers only ──────────────────────────────────────────────────────────
 *
 * Enforced by RLS on both tables, not by this component. Defining a field is a
 * layout decision — everybody on the board sees it — where setting one on a
 * card is ordinary work. An editor who could add options would grow the
 * vocabulary one card at a time, which is the exact failure closed options
 * exist to prevent. The button is hidden from non-managers as a courtesy; the
 * database is the actual answer.
 *
 * ── Deleting says what it will do, with the number attached ────────────────
 *
 * task_field_values cascades. Removing a field clears it from every card that
 * held it, so the confirm carries the count rather than a generic "are you
 * sure" that teaches people to click through it.
 *
 * ── Colour is editable; the label is not ───────────────────────────────────
 *
 * Options created here used to come out neutral grey with no way to change
 * them, so a board built by hand could never look like one built from the
 * Social Media Style template. Each chip now carries a colour control.
 *
 * Deliberately no rename beside it. Cards hold an option by ID, so renaming
 * one rewrites what every card carrying it appears to say — the vocabulary a
 * board agreed on would change underneath it, with no trace. Colour changes
 * how an option looks and nothing about what it means, which is why one is
 * offered and the other is not.
 */
export function FieldManager({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const definitions = useProjectFields(projectId);
  const values = useProjectFieldValues(projectId);

  const fields = definitions.data ?? [];
  const usage = fieldUsage(values.data ?? []);

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Fields…
      </Button>
      <Dialog
        open={open}
        title="Fields on this project"
        wide
        onClose={() => setOpen(false)}
        footer={
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Done
          </Button>
        }
      >
        <p className="field-manager-hint">
          Fields are the tags every card on this board can carry — Platform, Format, whatever this
          team actually sorts by. Changes here apply to the whole project.
        </p>

        {fields.length === 0 ? (
          <p className="field-manager-hint">
            This project has no fields yet. Add one below and it appears on every card.
          </p>
        ) : (
          <ul className="field-manager-list">
            {fields.map((field) => (
              <li key={field.field_id}>
                <FieldRow
                  projectId={projectId}
                  field={field}
                  otherNames={fields
                    .filter((other) => other.field_id !== field.field_id)
                    .map((other) => other.name)}
                  cards={usage.byField.get(field.field_id) ?? 0}
                  cardsPerOption={usage.byOption}
                />
              </li>
            ))}
          </ul>
        )}

        <NewFieldForm projectId={projectId} existingNames={fields.map((field) => field.name)} />
      </Dialog>
    </>
  );
}

/**
 * One field: its name, its options, and the two ways to remove things.
 *
 * Rename is a text box with a Save that only appears once the text differs.
 * An always-live autosave here would rename the field on every keystroke and
 * put "P", "Pl", "Pla" through the unique index on the way to "Platform".
 */
function FieldRow({
  projectId,
  field,
  otherNames,
  cards,
  cardsPerOption,
}: {
  projectId: string;
  field: FieldDefinition;
  otherNames: string[];
  cards: number;
  cardsPerOption: Map<string, number>;
}) {
  const rename = useRenameField(projectId);
  const removeField = useDeleteField(projectId);
  const addOption = useAddOption(projectId);
  const removeOption = useDeleteOption(projectId);
  const setColor = useSetOptionColor(projectId);

  const [name, setName] = useState(field.name);
  const [newOption, setNewOption] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_FIELD_COLOR);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  const renamed = name.trim() !== field.name;
  const renameError = renamed ? nameProblem(name, otherNames) : null;
  const optionError =
    newOption.trim() === ''
      ? null
      : optionProblem(
          newOption,
          field.options.map((option) => option.label),
        );
  return (
    <div className="field-manager-field">
      <div className="field-manager-name-row">
        <Input
          label="Field name"
          labelHidden
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={renameError ?? undefined}
        />
        {field.is_multi ? <span className="field-manager-multi">choose any</span> : null}
        {renamed && renameError === null ? (
          <Button
            size="sm"
            disabled={rename.isPending}
            onClick={() => {
              setError('');
              rename.mutate(
                { fieldId: field.field_id, name },
                { onError: (cause) => setError(cause.message) },
              );
            }}
          >
            {rename.isPending ? 'Saving…' : 'Rename'}
          </Button>
        ) : null}
      </div>

      {/* --swatch, not --on. The picker uses --on to mean "this card holds
          this option"; here the chips are the vocabulary itself and the colour
          is what somebody is choosing, so they need to render in it without
          borrowing a class that means something else. */}
      <div className="field-manager-options">
        {field.options.map((option) => (
          <span key={option.id} className="field-manager-option">
            <span className={`field-chip field-chip--${option.color_key} field-chip--swatch`}>
              {option.label}
              <button
                type="button"
                className="field-manager-remove"
                aria-label={`Remove option ${option.label}`}
                disabled={removeOption.isPending}
                onClick={() => {
                  setError('');
                  removeOption.mutate(option.id, { onError: (cause) => setError(cause.message) });
                }}
              >
                ×
              </button>
            </span>
            {/* Saves on change rather than behind a Save button. There is one
                value, it is chosen from a closed list, and it cannot be typed
                wrong — the reason rename has a button is that a half-typed
                name is a real state, and a half-chosen colour is not. */}
            <Select
              label={`Colour for ${option.label}`}
              labelHidden
              value={option.color_key}
              disabled={setColor.isPending}
              options={colorChoices(option.color_key).map((color) => ({
                value: color.key,
                label: color.label,
              }))}
              onChange={(event) => {
                setError('');
                setColor.mutate(
                  { optionId: option.id, colorKey: event.target.value },
                  { onError: (cause) => setError(cause.message) },
                );
              }}
            />
          </span>
        ))}
      </div>

      <div className="field-manager-add-option">
        <Input
          label={`Add an option to ${field.name}`}
          labelHidden
          placeholder="Add an option"
          value={newOption}
          onChange={(event) => setNewOption(event.target.value)}
          error={optionError ?? undefined}
        />
        {/* Wrapped, so it is not a direct .ui-field child of the row — that
            selector gives the label input flex: 1, and a colour name does not
            want half the row. */}
        <span className="field-manager-color-choice">
          <Select
            label={`Colour for the new option on ${field.name}`}
            labelHidden
            value={newColor}
            options={colorChoices(newColor).map((color) => ({
              value: color.key,
              label: color.label,
            }))}
            onChange={(event) => setNewColor(event.target.value)}
          />
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={newOption.trim() === '' || optionError !== null || addOption.isPending}
          onClick={() => {
            setError('');
            addOption.mutate(
              { fieldId: field.field_id, label: newOption, colorKey: newColor },
              {
                onSuccess: () => {
                  setNewOption('');
                  // The colour deliberately stays. Somebody adding four
                  // platforms in green wants the fourth to be green too, and
                  // resetting to grey each time makes the control feel like it
                  // did not take.
                },
                onError: (cause) => setError(cause.message),
              },
            );
          }}
        >
          Add
        </Button>
      </div>

      {/* Two steps, and the second one carries the count. A single Remove
          button beside a field somebody spent a month tagging is how a board
          loses a month of tagging. */}
      {confirming ? (
        <p className="field-manager-confirm">
          Removing “{field.name}” clears it from {cardCountLabel(cards)}. This cannot be undone.
          <Button
            variant="danger"
            size="sm"
            disabled={removeField.isPending}
            onClick={() => {
              setError('');
              removeField.mutate(field.field_id, { onError: (cause) => setError(cause.message) });
            }}
          >
            {removeField.isPending ? 'Removing…' : 'Remove it'}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setConfirming(false)}>
            Keep it
          </Button>
        </p>
      ) : (
        <p className="field-manager-usage">
          On {cardCountLabel(cards)}.
          <button
            type="button"
            className="field-manager-danger-link"
            onClick={() => setConfirming(true)}
          >
            Remove field
          </button>
        </p>
      )}

      {/* Per-option counts, so removing one option is as informed as removing
          the field. Kept as a quiet line rather than a badge on every chip,
          which would make the vocabulary harder to read than the numbers. */}
      {field.options.some((option) => (cardsPerOption.get(option.id) ?? 0) > 0) ? (
        <p className="field-manager-usage">
          {field.options
            .filter((option) => (cardsPerOption.get(option.id) ?? 0) > 0)
            .map(
              (option) => `${option.label}: ${cardCountLabel(cardsPerOption.get(option.id) ?? 0)}`,
            )
            .join(' · ')}
        </p>
      ) : null}

      {error === '' ? null : <InlineError>{error}</InlineError>}
    </div>
  );
}

/**
 * A new field and its first options, created together.
 *
 * Together on purpose: a field with no options is an empty dropdown, and the
 * board would show a heading nobody can answer. One line per option because
 * comma-separated text means somebody's option can never contain a comma.
 */
function NewFieldForm({
  projectId,
  existingNames,
}: {
  projectId: string;
  existingNames: string[];
}) {
  const create = useCreateField(projectId);
  const [name, setName] = useState('');
  const [isMulti, setIsMulti] = useState(false);
  const [options, setOptions] = useState('');
  const [error, setError] = useState('');

  const problem = name.trim() === '' ? null : nameProblem(name, existingNames);
  const parsed = options
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
  const ready = name.trim() !== '' && problem === null && parsed.length > 0;

  return (
    <div className="field-manager-new">
      <h3 className="field-manager-new-title">Add a field</h3>
      <Input
        label="Field name"
        placeholder="Platform"
        value={name}
        onChange={(event) => setName(event.target.value)}
        error={problem ?? undefined}
      />
      <label className="field-manager-multi-choice">
        <input
          type="checkbox"
          checked={isMulti}
          onChange={(event) => setIsMulti(event.target.checked)}
        />
        <span>
          A card can hold more than one
          <span className="field-manager-hint-inline">
            Platform yes — a post goes to three of them. Format no — it is one thing or the other.
          </span>
        </span>
      </label>
      <Textarea
        label="Options, one per line"
        rows={4}
        placeholder={'Instagram\nTikTok\nYouTube'}
        value={options}
        onChange={(event) => setOptions(event.target.value)}
      />
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <Button
        disabled={!ready || create.isPending}
        onClick={() => {
          setError('');
          create.mutate(
            { name, isMulti, options: parsed },
            {
              onSuccess: () => {
                setName('');
                setOptions('');
                setIsMulti(false);
              },
              onError: (cause) => setError(cause.message),
            },
          );
        }}
      >
        {create.isPending ? 'Adding…' : 'Add field'}
      </Button>
      {name.trim() !== '' && parsed.length === 0 ? (
        <p className="field-manager-hint">
          Give it at least one option. A field with none is a heading nobody can answer.
        </p>
      ) : null}
    </div>
  );
}
